import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import useRBAC from "../hooks/useRBAC";
import { callApi } from "../api/api";

import {
  Users,
  Shield,
  Database,
  Link2,
  GitBranch,
  GitMerge,
  ClipboardList,
  FileSearch,
  Tags,
  Building2,
  Clock,
  Calendar,
} from "lucide-react";

function accentTint(hex, alpha = 0.08) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ManageHubCard({ card, onNavigate, loading }) {
  const [hovered, setHovered] = useState(false);
  const Icon = card.icon;
  const tint = accentTint(card.accent);
  const showBadge = card.hasCount;

  return (
    <div
      style={{
        ...styles.card,
        borderLeft: hovered ? `6px solid ${card.accent}` : "6px solid transparent",
        borderColor: hovered ? card.accent : "#e2e8f0",
        transform: hovered ? "translateY(-4px)" : "none",
        boxShadow: hovered ? "0 12px 28px rgba(15,23,42,0.12)" : "none",
      }}
      onClick={() => onNavigate(card.path)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.cardTop}>
        <div
          style={{
            ...styles.iconBox,
            background: hovered ? tint : "#f1f5f9",
          }}
        >
          <Icon size={20} color={hovered ? card.accent : "#64748b"} />
        </div>

        {showBadge && (
          <div
            style={{
              ...styles.count,
              background: hovered ? tint : "#f1f5f9",
              color: hovered ? card.accent : "#64748b",
              minWidth: "36px",
              minHeight: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            {loading || card.count === null || card.count === undefined ? (
              <span
                style={{
                  display: "inline-block",
                  width: "13px",
                  height: "13px",
                  borderRadius: "50%",
                  border: "2px solid #cbd5e1",
                  borderTopColor: card.accent || "#2563eb",
                  animation: "manageSpin 0.75s linear infinite",
                }}
              />
            ) : (
              <p style={styles.countText}>{card.count}</p>
            )}
          </div>
        )}
      </div>

      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle}>{card.title}</h3>
        <p style={styles.cardDesc}>{card.desc}</p>
      </div>
    </div>
  );
}

export default function Manage() {
  const navigate = useNavigate();
  const { hasAccess } = useRBAC();
  const { instance, accounts } = useMsal();

  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    users: null,
    roles: null,
    resources: null,
    userRoles: null,
    roleResources: null,
    departments: null,
    lookups: null,
    holidays: null,
    docRoleMappings: null,
    workflowOrders: null,
    jobs: null,
  });

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await callApi(instance, accounts, "GET", "/api/admin/dashboard");
      const d = res.data?.data || res.data || {};
      const s = d.stats || d;
      setStats({
        users: s.users ?? 0,
        roles: s.roles ?? 0,
        resources: s.resources ?? 0,
        userRoles: s.userRoles ?? 0,
        roleResources: s.roleResources ?? 0,
        departments: s.departments ?? 0,
        lookups: s.lookups ?? 0,
        holidays: s.holidays ?? 0,
        docRoleMappings: s.docRoleMappings ?? 0,
        workflowOrders: s.workflowOrders ?? 0,
        jobs: s.jobs ?? 0,
      });
    } catch {
      /* non-fatal */
    } finally {
      setStatsLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const cards = [
    {
      title: "Departments",
      desc: "Manage organizational departments & interactive reporting hierarchy",
      icon: Building2,
      path: "/departments",
      resource: "ADMIN",
      access: "READ",
      hasCount: true,
      count: stats.departments,
      accent: "#0284c7",
    },
    {
      title: "Users",
      desc: "View, add, and manage portal users",
      icon: Users,
      path: "/users",
      resource: "ADMIN",
      access: "READ",
      hasCount: true,
      count: stats.users,
      accent: "#2563eb",
    },
    {
      title: "Roles",
      desc: "Define roles and their descriptions",
      icon: Shield,
      path: "/roles",
      resource: "ADMIN",
      access: "READ",
      hasCount: true,
      count: stats.roles,
      accent: "#7c3aed",
    },
    {
      title: "Resources",
      desc: "Manage protected resources & access levels",
      icon: Database,
      path: "/resources",
      resource: "ADMIN",
      access: "READ",
      hasCount: true,
      count: stats.resources,
      accent: "#0891b2",
    },
    {
      title: "Lookup Management",
      desc: "Manage document types, lookup codes, LOBs and metadata values",
      icon: Tags,
      path: "/lookups",
      resource: "ADMIN",
      access: "READ",
      hasCount: true,
      count: stats.lookups,
      accent: "#db2777",
    },
    {
      title: "User-Role Assign",
      desc: "Assign roles to users",
      icon: Link2,
      path: "/assign-user-role",
      resource: "ADMIN",
      access: "WRITE",
      hasCount: true,
      count: stats.userRoles,
      accent: "#0d9488",
    },
    {
      title: "Role-Resource Assign",
      desc: "Map resources onto roles",
      icon: GitBranch,
      path: "/assign-role-resource",
      resource: "ADMIN",
      access: "WRITE",
      hasCount: true,
      count: stats.roleResources,
      accent: "#4f46e5",
    },
    {
      title: "Back Scheduler",
      desc: "Schedule and manage automated background tasks and DevOps integration jobs.",
      icon: Clock,
      path: "/scheduler",
      resource: "ADMIN",
      access: "WRITE",
      hasCount: true,
      count: stats.jobs,
      accent: "#8b5cf6",
    },
    {
      title: "Role & Document Type Mapping",
      desc: "Configure strict operational pairings between Document Types and System Roles (1:N).",
      icon: Link2,
      path: "/role-document-mapping",
      resource: "ADMIN",
      access: "WRITE",
      hasCount: true,
      count: stats.docRoleMappings,
      accent: "#047857",
    },
    {
      title: "Manage Workflows",
      desc: "Configure and reorder System Role routing priority order for document assignment workflows.",
      icon: GitMerge,
      path: "/manage-workflows",
      resource: "ADMIN",
      access: "WRITE",
      hasCount: true,
      count: stats.workflowOrders,
      accent: "#6366f1",
    },
    {
      title: "Holidays",
      desc: "Manage and view organization holidays for the year.",
      icon: Calendar,
      path: "/holidays",
      resource: "HOLIDAYS",
      access: "READ",
      hasCount: true,
      count: stats.holidays,
      accent: "#f59e0b",
    },
    {
      title: "Access Review",
      desc: "Inspect effective access for any user",
      icon: ClipboardList,
      path: "/access-review",
      resource: "ADMIN",
      access: "READ",
      hasCount: false,
      count: null,
      accent: "#059669",
    },
    {
      title: "Audit Logs",
      desc: "Track system activity and access history",
      icon: FileSearch,
      path: "/audit-logs",
      resource: "ADMIN",
      access: "READ",
      hasCount: false,
      count: null,
      accent: "#78716c",
    },
  ];

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes manageSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={styles.header}>
        <div>
          <div style={styles.breadcrumb}>Administration &gt; Manage</div>
          <h1 style={styles.title}>Manage</h1>
          <p style={styles.subtitle}>
            Administer users, roles, resources and their assignments.
          </p>
        </div>
      </div>

      <div style={styles.grid}>
        {cards
          .filter((c) => hasAccess(c.resource, c.access))
          .map((c) => (
            <ManageHubCard key={c.path} card={c} onNavigate={navigate} loading={statsLoading} />
          ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "clamp(12px, 4vw, 24px)",
    boxSizing: "border-box",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  header: {
    marginBottom: "20px",
  },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },
  subtitle: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "clamp(12px, 3vw, 18px)",
    width: "100%",
    boxSizing: "border-box",
    alignItems: "stretch",
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "clamp(14px, 3vw, 18px)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
    minHeight: "148px",
    boxSizing: "border-box",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
  },
  cardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },
  iconBox: {
    padding: "10px",
    borderRadius: "10px",
    flexShrink: 0,
    display: "inline-flex",
  },
  count: {
    padding: "6px 10px",
    borderRadius: "8px",
    flexShrink: 0,
  },
  countText: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
  },
  cardTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#0f172a",
  },
  cardDesc: {
    marginTop: "5px",
    fontSize: "13px",
    color: "#64748b",
    lineHeight: "1.4",
  },
};
